import { TOTAL_FRAMES, CLIP_DURATION_SECONDS } from '@/animation/SceneSections';

/**
 * Left-side scroll/timeline HUD (2026-07-17, user request: "add one counter in left side i want
 * to analyse scroll progress so i can tell you perfect when things i want animated").
 *
 * The number that actually matters here is **CLIP FRAME**. `SceneSections.SECTIONS` is authored
 * in frames of the baked Blender camera clip (`startFrame`), and its current boundaries are
 * still guesses — so to pin down where a scene should swap, where the white flash should fire,
 * or where anything else should trigger, scrub to the moment and read the frame off this panel.
 *
 * SCROLL is the raw 0-1 scroll position. CLIP FRAME maps that into the camera clip's real
 * timeline, accounting for the intro fly-in window (`ScrollCameraTest.introStartFraction`) —
 * so it's directly comparable to a frame number in Blender's timeline.
 *
 * Purely informational: `pointer-events: none`, so it can never eat a scroll/click.
 */
export class ScrollReadout {
  private readonly root: HTMLDivElement;
  private readonly values: Record<string, HTMLSpanElement> = {};
  /** Throttle DOM writes — the numbers are unreadable at 60fps anyway. */
  private accumulator = 0;
  private static readonly INTERVAL = 1 / 15; // seconds

  constructor() {
    this.root = document.createElement('div');
    this.root.id = 'scroll-readout';
    this.root.style.cssText = [
      'position:fixed',
      'left:16px',
      'top:50%',
      'transform:translateY(-50%)',
      'z-index:40',
      'pointer-events:none',
      'font-family:ui-monospace,SFMono-Regular,Menlo,Consolas,monospace',
      'font-size:11px',
      'line-height:1.55',
      'letter-spacing:0.04em',
      'color:#eaf2ff',
      'background:rgba(10,16,26,0.62)',
      'backdrop-filter:blur(6px)',
      '-webkit-backdrop-filter:blur(6px)',
      'border:1px solid rgba(255,255,255,0.14)',
      'border-radius:8px',
      'padding:10px 12px',
      'min-width:172px',
      'box-shadow:0 6px 22px rgba(0,0,0,0.32)',
    ].join(';');

    this.root.appendChild(this.heading());
    this.row('SCROLL', 'scroll');
    this.row('CLIP FRAME', 'frame');
    this.row('CLIP TIME', 'time');
    this.row('SECTION', 'section');
    this.root.appendChild(this.footnote());

    document.body.appendChild(this.root);
  }

  private heading(): HTMLDivElement {
    const el = document.createElement('div');
    el.textContent = 'TIMELINE';
    el.style.cssText = [
      'font-size:9px',
      'letter-spacing:0.18em',
      'color:rgba(234,242,255,0.5)',
      'margin-bottom:6px',
    ].join(';');
    return el;
  }

  private footnote(): HTMLDivElement {
    const el = document.createElement('div');
    el.textContent = `of ${TOTAL_FRAMES} @ 24fps`;
    el.style.cssText = [
      'margin-top:6px',
      'font-size:9px',
      'color:rgba(234,242,255,0.4)',
    ].join(';');
    return el;
  }

  private row(label: string, key: string): void {
    const row = document.createElement('div');
    row.style.cssText = 'display:flex;justify-content:space-between;gap:14px';

    const l = document.createElement('span');
    l.textContent = label;
    l.style.color = 'rgba(234,242,255,0.55)';

    const v = document.createElement('span');
    v.textContent = '—';
    v.style.cssText = 'font-variant-numeric:tabular-nums;font-weight:600';

    row.append(l, v);
    this.root.appendChild(row);
    this.values[key] = v;
  }

  /**
   * @param progress      raw scroll progress, 0-1
   * @param clipFraction  progress mapped into the camera clip's real timeline, 0-1
   * @param sectionName   the current `SceneSections` section label
   * @param delta         frame delta (seconds) — used only to throttle DOM writes
   */
  update(progress: number, clipFraction: number, sectionName: string, delta: number): void {
    this.accumulator += delta;
    if (this.accumulator < ScrollReadout.INTERVAL) return;
    this.accumulator = 0;

    const frame = clipFraction * TOTAL_FRAMES;
    this.values.scroll!.textContent = `${progress.toFixed(4)}  ${(progress * 100).toFixed(1)}%`;
    this.values.frame!.textContent = frame.toFixed(1);
    this.values.time!.textContent = `${(clipFraction * CLIP_DURATION_SECONDS).toFixed(2)}s`;
    this.values.section!.textContent = sectionName;
  }

  setVisible(visible: boolean): void {
    this.root.style.display = visible ? '' : 'none';
  }

  dispose(): void {
    this.root.remove();
  }
}
